// src/features/developers/api.ts
//
// The developers endpoints and the staff key management, through the application's authenticated
// client (token, impersonation header). The client is loaded on first use so the shell does not
// pull it in just to know the route exists. Every call is replaceable as a unit, which is how the
// screenshot harness answers with fixtures.

import { uiApiUrl } from '@/utils/uiApiUrl';
import type { AccessKey, CreateKeyRequest, CreatedAccessKey, DeveloperInfo } from './types';

export interface ApiFailure extends Error {
  status: number;
  code?: string;
}

const authorizedFetch = () => import('@/providers/dataProvider').then((m) => m.authorizedFetch);

const failure = async (response: Response): Promise<ApiFailure> => {
  let code: string | undefined;
  try {
    const body = (await response.json()) as { error?: { code?: string } };
    code = body?.error?.code;
  } catch {
    // Not JSON: the status alone says it.
  }
  return Object.assign(new Error(code ?? String(response.status)), { status: response.status, code });
};

const read = async (path: string): Promise<Response> => {
  const response = await (await authorizedFetch())(uiApiUrl(path), { headers: new Headers({ Accept: 'application/json' }) });
  if (!response.ok) throw await failure(response);
  return response;
};

const unwrap = <T>(body: unknown): T => {
  const envelope = body as { success?: boolean; data?: T };
  return (envelope && typeof envelope === 'object' && 'data' in envelope ? envelope.data : body) as T;
};

const send = async <T>(path: string, method: 'POST', body?: unknown): Promise<T> => {
  const response = await (await authorizedFetch())(uiApiUrl(path), {
    method,
    headers: new Headers({ Accept: 'application/json' }),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return unwrap<T>(await response.json());
};

export const developersApi = {
  info: async (): Promise<DeveloperInfo> => unwrap<DeveloperInfo>(await (await read('developers/info')).json()),
  /** The OpenAPI document, raw: parsed for the reference, as text for the download. */
  openApiText: async (): Promise<string> => (await read('developers/openapi.json')).text(),
  postmanCollectionText: async (): Promise<string> => (await read('developers/postman/collection.json')).text(),
  postmanEnvironmentText: async (environment: string): Promise<string> =>
    (await read(`developers/postman/environment.json?env=${encodeURIComponent(environment)}`)).text(),

  listKeys: async (): Promise<AccessKey[]> => unwrap<AccessKey[]>(await (await read('developers/keys')).json()),
  createKey: (request: CreateKeyRequest): Promise<CreatedAccessKey> => send<CreatedAccessKey>('developers/keys', 'POST', request),
  revokeKey: (id: string): Promise<AccessKey> => send<AccessKey>(`developers/keys/${encodeURIComponent(id)}/revoke`, 'POST'),
};
